// FILE: src/services/supabaseClient.js
// Thin wrapper that instantiates the Supabase client for the active environment.
//
// Environment resolution — FAIL CLOSED:
//   • Netlify injects REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY at
//     build time for every deploy context (production, branch-deploy,
//     deploy-preview). Local dev reads them from .env; Jest from .env.test.
//   • If either variable is missing this module THROWS instead of silently
//     falling back to the production project. The old prod fallback let
//     env-less CI builds (Playwright E2E) pull full question banks from the
//     production database on every test run — the root cause of the June 2026
//     egress overage (docs/egress-audit-2026-07-02.md).
//   • E2E guard: a build with REACT_APP_E2E_BYPASS_AUTH=true must NEVER point
//     at the production project. See docs/e2e-environments.md.

import { createClient } from '@supabase/supabase-js';

// Project refs (public identifiers, safe to commit).
export const PROD_PROJECT_REF = 'dcvpogwkwtbhlsyddbue';
export const STAGING_PROJECT_REF = 'trnfxirkyjczucntavxt';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error(
    '[supabaseClient] REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY ' +
      'must be set at build time. There is intentionally no production ' +
      'fallback — see docs/e2e-environments.md for the required variables.'
  );
}

if (
  process.env.REACT_APP_E2E_BYPASS_AUTH === 'true' &&
  SUPABASE_URL.includes(PROD_PROJECT_REF)
) {
  throw new Error(
    '[supabaseClient] E2E builds (REACT_APP_E2E_BYPASS_AUTH=true) must never ' +
      'target the production Supabase project.'
  );
}

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// Exported for diagnostics — the Admin panel can surface which project is
// active so reviewers can sanity-check they're not editing prod while on the
// staging URL.
export const SUPABASE_PROJECT_URL = SUPABASE_URL;

// True when the build targets the staging Supabase project.
// Used to gate admin tools that should never appear in prod.
export const IS_STAGING = SUPABASE_URL.includes(STAGING_PROJECT_REF);
